<template>
    <div class="profile-container">
        <v-offline
            online-class="online"
            offline-class="offline"
            @detected-condition="amIOnline">
            <template v-slot:[onlineSlot] :slot-name="onlineSlot">
                {{ onlineOfflineMsg }}
            </template>
            <template v-slot:[offlineSlot] :slot-name="offlineSlot">
                {{ onlineOfflineMsg }}
            </template>
        </v-offline>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
            <div class="loader-spinner"></div>
            <p>Loading profile...</p>
        </div>

        <!-- Profile Content -->
        <div v-else class="profile-content">
            <!-- Profile Card -->
            <div class="profile-card">
                <!-- Profile Image Section -->
                <div class="profile-image-section">
                    <div class="profile-image-wrapper">
                        <template v-if="!uploadNew">
                            <div class="profile-image-container">
                                <template v-if="uploadURL === null">
                                    <div class="profile-avatar">
                                        <span class="avatar-initials">
                                            {{ model.user.first_name.charAt(0) + model.user.last_name.charAt(0) }}
                                        </span>
                                    </div>
                                </template>
                                <template v-else>
                                    <img :src="uploadURL" class="profile-image" alt="Profile picture"/>
                                </template>
                                <button class="change-photo-btn" @click="toggleUploadNew">
                                    <i class="nc-icon nc-camera-compact"></i>
                                    <span>Change Photo</span>
                                </button>
                            </div>
                        </template>
                        <template v-else>
                            <div class="upload-section">
                                <div class="upload-header">
                                    <h4>Upload Profile Photo</h4>
                                    <p class="upload-hint">Use a square image (1:1 ratio) for best results</p>
                                    <span v-if="uploadingLabel !== 'None'" class="upload-status">
                                        {{ uploadingLabel }}
                                    </span>
                                </div>
                                <image-upload
                                    :target="imageUploadApiUrl"
                                    selector="example"
                                    :auto-push="true">
                                </image-upload>
                                <button type="button" class="cancel-upload-btn" @click="toggleUploadNew">
                                    <i class="nc-icon nc-simple-remove"></i>
                                    Cancel
                                </button>
                            </div>
                        </template>
                    </div>
                </div>

                <!-- Profile Form -->
                <div class="profile-form-section">
                    <form @submit.prevent="validateAndSubmit">
                        <!-- Email Field -->
                        <div class="form-field">
                            <fieldset>
                                <div class="form-group">
                                    <fg-input
                                        disabled
                                        type="text"
                                        label="Email"
                                        v-model="model.user.email">
                                    </fg-input>
                                </div>
                            </fieldset>
                            <a @click="openUpdateEmailDialog" class="change-email-link">
                                <i class="nc-icon nc-email-85"></i>
                                Change Email
                            </a>
                        </div>

                        <!-- First Name -->
                        <div class="form-field">
                            <fieldset>
                                <div class="form-group">
                                    <fg-input
                                        type="text"
                                        v-validate="modelValidations.firstName"
                                        :error="getError('First Name')"
                                        name="First Name"
                                        label="First Name"
                                        v-model="model.user.first_name">
                                    </fg-input>
                                </div>
                            </fieldset>
                        </div>

                        <!-- Last Name -->
                        <div class="form-field">
                            <fieldset>
                                <div class="form-group">
                                    <fg-input
                                        type="text"
                                        v-validate="modelValidations.lastName"
                                        :error="getError('Last Name')"
                                        name="Last Name"
                                        label="Last Name"
                                        v-model="model.user.last_name">
                                    </fg-input>
                                </div>
                            </fieldset>
                        </div>

                        <!-- Phone -->
                        <div class="form-field">
                            <fieldset>
                                <div class="form-group">
                                    <fg-input
                                        type="text"
                                        v-validate="modelValidations.phone"
                                        :error="getError('Phone')"
                                        name="Phone"
                                        label="Phone"
                                        v-model="model.user.phone">
                                    </fg-input>
                                </div>
                            </fieldset>
                        </div>

                        <!-- Update Button -->
                        <div class="form-actions">
                            <button 
                                type="submit"
                                :disabled="!onLine || button.isDisabled" 
                                class="update-btn">
                                <i class="nc-icon nc-check-2"></i>
                                <span v-if="button.isDisabled">Updating...</span>
                                <span v-else>Update Profile</span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <!-- Organisation Application Banner -->
            <div class="organisation-banner">
                <div class="banner-content">
                    <i class="nc-icon nc-badge"></i>
                    <div class="banner-text">
                        <h4>Want your own organisation?</h4>
                        <p>Apply now to create and manage your own organisation</p>
                    </div>
                </div>
                <router-link :to="{name: 'organisationSettings'}" class="banner-btn">
                    Apply Now
                    <i class="nc-icon nc-minimal-right"></i>
                </router-link>
            </div>

            <!-- Organisations Section -->
            <div class="organisations-section">
                <div class="section-header">
                    <h3>
                        <i class="nc-icon nc-bank"></i>
                        Joined Organisations
                    </h3>
                </div>

                <div v-if="loadingOrgs" class="loading-orgs">
                    <div class="loader-spinner small"></div>
                    <span>Loading organisations...</span>
                </div>

                <div v-else-if="joinedOrganisations.length > 0" class="organisations-list">
                    <div v-for="(org, index) in joinedOrganisations" 
                         :key="org.organisation.id" 
                         class="organisation-card">
                        <div class="org-icon">
                            <i class="nc-icon nc-bank"></i>
                        </div>
                        <div class="org-details">
                            <h4 class="org-name">{{ org.organisation.name }}</h4>
                            <p class="org-director">
                                <i class="nc-icon nc-single-02"></i>
                                Director: {{ org.organisation.director.email }}
                            </p>
                        </div>
                        <button 
                            @click="leaveOrganisation(org.organisation.id, index)" 
                            class="leave-btn"
                            title="Leave organisation">
                            <i class="nc-icon nc-simple-remove"></i>
                        </button>
                    </div>
                </div>

                <div v-else class="empty-state">
                    <div class="empty-icon">
                        <i class="nc-icon nc-bank"></i>
                    </div>
                    <h4>No Organisations Yet</h4>
                    <p>You have not joined any organisations. Organisations enable collaboration, allowing you to work with others on projects.</p>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import mixin from '../../mixins/GlobalMixin';
import VOffline from 'v-offline';
import ImageUpload from '../ImageUpload.vue';

import Swal from 'sweetalert2';

export default {

    components: {
        'image-upload': ImageUpload,
        VOffline
    },
    mixins: [mixin],
    data() {
        return {
            loading: true,
            loadingOrgs: true,

            model: {
                user: {},
            },

            button: {
                isDisabled: false,
            },

            modelValidations: {
                firstName: {
                    alpha: true,
                },
                lastName: {
                    alpha: true,
                },
                phone: {
                    digits: 10
                },
            },

            imageUploadApiUrl: window.API_BASE_URL + '/UploadToS3/upload/profile_picture',
            uploadURL: null,
            uploadingLabel: 'None',
            uploadPending: false,
            uploadNew: false,

            joinedOrganisations: [],

        }
    },
    methods: {
        init() {
            let vm = this;

            Bus.$on('example-did-upload', (error, data, response) => {
                if (error) {
                    toastr.error(error);
                } else {
                    this.uploadURL = response.src;
                    this.uploadingLabel = "Uploaded";
                    this.uploadPending = false;
                }
            });

            Bus.$on('example-did-failed', (error, data, response) => {
                new Swal({
                    title: `Upload Failed`,
                    text: 'Image size should not be more than 2MB',
                    buttonsStyling: false,
                    confirmButtonClass: 'btn btn-danger btn-fill',
                    type: 'warning',
                });
            });

            Bus.$on('example-is-saving', () => {
                this.uploadPending = true;
                this.uploadingLabel = "Uploading Image...";
            });

            vm.getUserDetails();

            vm.getOrganisations();


        },
        getUserDetails() {
            let vm = this;
            vm.loading = true;
            axios.get('/user').then(response => {
                if (response.data.response.error.error_code == 0) {
                    vm.model.user = response.data.response.data.user;
                    if (response.data.response.data.user.photo_url != null) {
                        if (response.data.response.data.user.photo_url != '') {
                            vm.uploadURL = response.data.response.data.user.photo_url;
                        }
                    } else {
                        vm.uploadURL = null;
                    }
                }
            }).catch(error => {
                console.error('Error loading user details:', error);
            }).finally(() => {
                vm.loading = false;
            });
        },
        getOrganisations() {
            let vm = this;
            vm.loadingOrgs = true;
            axios.get('/user/organisations')
                .then(response => {
                    if (response.data.response.error.error_code == 0) {
                        vm.joinedOrganisations = response.data.response.data.joined_organisations;
                    }
                }).catch(error => {
                    console.error('Error loading organisations:', error);
                }).finally(() => {
                    vm.loadingOrgs = false;
                });
        },
        toggleUploadNew() {
            this.uploadNew = !this.uploadNew;
        },
        validateAndSubmit() {
            let vm = this;

            if (vm.uploadPending) {
                alert('Please wait image is being uploaded');
                return;
            }

            vm.$validator.validateAll().then((result) => {
                if (result) {
                    vm.button.isDisabled = true;
                    axios.put('/user/update', {
                        first_name: vm.model.user.first_name,
                        last_name: vm.model.user.last_name,
                        photo_url: vm.uploadURL,
                        phone: vm.model.user.phone,
                    }).then(response => {
                        vm.button.isDisabled = false;
                        if (response.data.response.error.error_code == 0) {
                            vm.showAlert('Success', response.data.response.error.msg, true);
                            window.DefaultUserImage = vm.uploadURL;
                            var userData = response.data.response.data.user;
                            var user_name = userData.first_name + ' ' + userData.last_name;
                            var user_image = userData.photo_url;
                            var user_email = userData.email;
                            var obj = {userName: user_name, userImage: user_image, userEmail: user_email}
                            localStorage.setItem('userdata', JSON.stringify(obj));
                            this.$store.dispatch('user/getUser');
                            this.$root.$emit('userDataSet');
                            Bus.$emit("userData", userData);
                        } else {
                            vm.showAlert('Error', response.data.response.error.error_msg, false);
                        }
                    }).catch(error => {
                        vm.button.isDisabled = false;
                    });
                }
            });
        },
        leaveOrganisation(orgId, index) {
            let vm = this;

            new Swal({
                title: 'Are you sure you want to leave this organisation?',
                text: `You won't be able to revert this!`,
                type: 'warning',
                showCancelButton: true,
                confirmButtonClass: 'btn btn-success btn-fill',
                cancelButtonClass: 'btn btn-danger btn-fill',
                confirmButtonText: 'Yes, Leave Org.',
                buttonsStyling: false,
            }).then(function (result) {
                if (result.value) {
                    axios.get('/organisation/leave/' + orgId)
                        .then(response => {
                            if (response.data.response.error.error_code == 0) {
                                vm.showAlert(response.data.response.error.msg, '', true);
                                vm.joinedOrganisations.splice(index, 1);
                                vm.getOrganisations();
                            } else {
                                vm.showAlert(response.data.response.error.error_msg, '', false);
                            }
                        })
                        .catch(error => {
                            if (error.response.data.response.error) {
                                vm.showAlert(response.data.response.error.error_msg, '', false);
                            }
                        });
                }
            });
        },
        openUpdateEmailDialog() {
            new Swal({
                title: 'Enter new email id',
                input: 'text',
                inputAttributes: {
                    autocapitalize: 'off'
                },
                showCancelButton: true,
                confirmButtonText: 'Change',
                showLoaderOnConfirm: true,
                preConfirm: (email) => {
                    return axios.put(`/user/update/email`, {
                        updated_email: email
                    })
                        .then(resp => {
                            return resp
                        })
                        .catch(error => {
                            return error
                        })
                },
                allowOutsideClick: () => !swal.isLoading()
            }).then((result) => {
                console.log("result")
                console.log(result)
                if (result.value.data) {
                    console.log(result)
                    if (result.value.data.response.error.error_code == 1) {
                        new Swal({
                            title: 'Error',
                            text: result.value.data.response.error.error_msg,
                            type: 'error',
                        })
                    } else {
                        new Swal({
                            title: result.value.data.response.error.msg,
                        })
                    }
                } else {
                    swal(
                        'Invalid email'
                    )
                }
            })
        }
    },
    mounted() {
        this.init();
    }

}

</script>
<style scoped>
.profile-container {
    padding: 20px;
}

/* Page Header */
.page-header {
    background: white;
    padding: 24px 32px;
    margin-bottom: 24px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.page-title {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    color: #333;
    display: flex;
    align-items: center;
    gap: 12px;
}

.page-title i {
    font-size: 28px;
    color: #1e88e5;
}

/* Loading State */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.loader-spinner {
    border: 3px solid #f3f3f3;
    border-top: 3px solid #1e88e5;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
}

.loader-spinner.small {
    width: 24px;
    height: 24px;
    border-width: 2px;
    margin-bottom: 0;
    margin-right: 8px;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.loading-state p {
    color: #999;
    margin: 0;
}

/* Profile Content */
.profile-content {
    max-width: 1000px;
    margin: 0 auto;
}

/* Profile Card */
.profile-card {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    overflow: hidden;
    margin-bottom: 24px;
}

/* Profile Image Section */
.profile-image-section {
    padding: 40px 20px;
    background: linear-gradient(to bottom, #f8f9fa 0%, white 100%);
    border-bottom: 1px solid #e8e8e8;
}

.profile-image-wrapper {
    display: flex;
    justify-content: center;
}

.profile-image-container {
    text-align: center;
    position: relative;
}

.profile-avatar {
    width: 140px;
    height: 140px;
    border-radius: 50%;
    background: #004837;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.avatar-initials {
    color: white;
    font-size: 56px;
    font-weight: bold;
    text-transform: uppercase;
}

.profile-image {
    width: 140px;
    height: 140px;
    border-radius: 50%;
    object-fit: cover;
    margin-bottom: 20px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border: 4px solid white;
}

.change-photo-btn {
    background: #1e88e5;
    color: white;
    border: none;
    border-radius: 24px;
    padding: 10px 24px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;
    box-shadow: 0 2px 6px rgba(30, 136, 229, 0.3);
}

.change-photo-btn:hover {
    background: #1976d2;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(30, 136, 229, 0.4);
}

.change-photo-btn i {
    font-size: 16px;
}

/* Upload Section */
.upload-section {
    text-align: center;
    padding: 20px;
}

.upload-header {
    margin-bottom: 20px;
}

.upload-header h4 {
    margin: 0 0 8px 0;
    font-size: 18px;
    font-weight: 600;
    color: #333;
}

.upload-hint {
    font-size: 13px;
    color: #999;
    margin: 0 0 8px 0;
}

.upload-status {
    display: inline-block;
    padding: 4px 12px;
    background: #e3f2fd;
    color: #1e88e5;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
}

.cancel-upload-btn {
    margin-top: 16px;
    background: #eb6532;
    color: white;
    border: none;
    border-radius: 20px;
    padding: 8px 20px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.3s ease;
}

.cancel-upload-btn:hover {
    background: #d95a2b;
    transform: translateY(-2px);
}

/* Profile Form Section */
.profile-form-section {
    padding: 32px;
}

.form-field {
    margin-bottom: 20px;
}

.change-email-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #1e88e5;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.3s ease;
}

.change-email-link:hover {
    color: #1976d2;
    text-decoration: underline;
}

.change-email-link i {
    font-size: 16px;
}

/* Form Actions */
.form-actions {
    margin-top: 32px;
    text-align: center;
}

.update-btn {
    background: #2a9055;
    color: white;
    border: none;
    border-radius: 24px;
    padding: 12px 48px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;
    box-shadow: 0 3px 8px rgba(42, 144, 85, 0.3);
}

.update-btn:hover:not(:disabled) {
    background: #239046;
    transform: translateY(-2px);
    box-shadow: 0 5px 14px rgba(42, 144, 85, 0.4);
}

.update-btn:disabled {
    background: #ccc;
    cursor: not-allowed;
    box-shadow: none;
}

.update-btn i {
    font-size: 16px;
}

/* Organisation Banner */
.organisation-banner {
    background: linear-gradient(to right, #1e88e5 0%, #1976d2 100%);
    border-radius: 8px;
    padding: 24px 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    box-shadow: 0 4px 12px rgba(30, 136, 229, 0.2);
}

.banner-content {
    display: flex;
    align-items: center;
    gap: 16px;
    color: white;
}

.banner-content > i {
    font-size: 36px;
    opacity: 0.9;
}

.banner-text h4 {
    margin: 0 0 4px 0;
    font-size: 18px;
    font-weight: 600;
}

.banner-text p {
    margin: 0;
    font-size: 14px;
    opacity: 0.9;
}

.banner-btn {
    background: white;
    color: #1e88e5;
    border: none;
    border-radius: 24px;
    padding: 10px 24px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;
    transition: all 0.3s ease;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.banner-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Organisations Section */
.organisations-section {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    overflow: hidden;
}

.section-header {
    padding: 24px 32px;
    border-bottom: 1px solid #e8e8e8;
}

.section-header h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #333;
    display: flex;
    align-items: center;
    gap: 10px;
}

.section-header h3 i {
    font-size: 24px;
    color: #1e88e5;
}

/* Loading Orgs */
.loading-orgs {
    padding: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
    font-size: 14px;
}

/* Organisations List */
.organisations-list {
    padding: 16px;
}

.organisation-card {
    display: flex;
    align-items: center;
    padding: 20px;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    margin-bottom: 12px;
    background: white;
    transition: all 0.3s ease;
}

.organisation-card:last-child {
    margin-bottom: 0;
}

.organisation-card:hover {
    background: #f8f9fa;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    transform: translateY(-2px);
}

.org-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #e3f2fd;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-right: 16px;
}

.org-icon i {
    font-size: 22px;
    color: #1e88e5;
}

.org-details {
    flex: 1;
}

.org-name {
    margin: 0 0 6px 0;
    font-size: 16px;
    font-weight: 600;
    color: #333;
}

.org-director {
    margin: 0;
    font-size: 13px;
    color: #666;
    display: flex;
    align-items: center;
    gap: 6px;
}

.org-director i {
    font-size: 14px;
}

.leave-btn {
    background: transparent;
    border: none;
    color: #999;
    cursor: pointer;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    flex-shrink: 0;
}

.leave-btn:hover {
    background: #ffebee;
    color: #eb6532;
    transform: rotate(90deg);
}

.leave-btn i {
    font-size: 18px;
}

/* Empty State */
.empty-state {
    padding: 60px 32px;
    text-align: center;
}

.empty-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: #e3f2fd;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
}

.empty-icon i {
    font-size: 36px;
    color: #1e88e5;
}

.empty-state h4 {
    margin: 0 0 12px 0;
    font-size: 18px;
    font-weight: 600;
    color: #333;
}

.empty-state p {
    margin: 0;
    font-size: 14px;
    color: #666;
    line-height: 1.6;
    max-width: 500px;
    margin: 0 auto;
}

/* Responsive */
@media (max-width: 768px) {
    .profile-container {
        padding: 12px;
    }

    .page-header {
        padding: 20px;
        margin-bottom: 16px;
    }

    .page-title {
        font-size: 20px;
    }

    .profile-form-section {
        padding: 24px 20px;
    }

    .organisation-banner {
        flex-direction: column;
        gap: 20px;
        padding: 20px;
        text-align: center;
    }

    .banner-content {
        flex-direction: column;
        gap: 12px;
    }

    .section-header {
        padding: 20px;
    }

    .organisations-list {
        padding: 12px;
    }
}

@media (max-width: 576px) {
    .page-title {
        font-size: 18px;
    }

    .page-title i {
        font-size: 22px;
    }

    .profile-avatar {
        width: 120px;
        height: 120px;
    }

    .avatar-initials {
        font-size: 48px;
    }

    .profile-image {
        width: 120px;
        height: 120px;
    }

    .profile-form-section {
        padding: 20px 16px;
    }

    .organisation-card {
        padding: 16px;
    }

    .org-icon {
        width: 40px;
        height: 40px;
    }

    .org-icon i {
        font-size: 18px;
    }

    .org-name {
        font-size: 15px;
    }

    .org-director {
        font-size: 12px;
    }

    .empty-state {
        padding: 40px 20px;
    }
}
</style>
